import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * Activity log for the tenant — `GET /v1/logs`.
 *
 * One row per API request, written by the backend's activity log middleware. The window
 * defaults to the last 7 days server-side, so an unfiltered first load is always bounded.
 *
 * `event_name` arrives as the backend's raw value (`create_app`, `app.published`, …) and is
 * rendered as-is rather than mapped to a label: the vocabulary grows with every endpoint, so a
 * lookup table here would be permanently out of date.
 *
 * Reading it requires the Administrator-only `log-view`, so callers outside Settings must
 * pass `enabled: false` when the user lacks it — otherwise every mount is a wasted 403.
 */
export type LogEntry = {
  id: string;
  occurred_at: string;
  event_name: string;
  method: string;
  path: string;
  /** Null for an unauthenticated request, e.g. a rejected sign-in. */
  user_id: number | null;
  status_code: number;
  duration_ms: number | null;
};

export type LogPage = {
  items: LogEntry[];
  total: number;
  limit: number;
  offset: number;
  /** Always null today; reserved so a later move to keyset paging is not a breaking change. */
  next_cursor: string | null;
};

export type LogFilters = {
  start?: string;
  end?: string;
  user_id?: number;
  event_name?: string;
  outcome?: "succeeded" | "failed";
  limit?: number;
  offset?: number;
};

export type LogsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  page: LogPage | null;
};

export const logsInitialState: LogsState = {
  status: STATUS.IDLE,
  error: null,
  page: null,
};

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isLogEntry(value: unknown): value is LogEntry {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "string" &&
    typeof row.occurred_at === "string" &&
    typeof row.event_name === "string" &&
    typeof row.method === "string" &&
    typeof row.path === "string" &&
    (row.user_id === null || isNumber(row.user_id)) &&
    isNumber(row.status_code) &&
    (row.duration_ms === null || isNumber(row.duration_ms))
  );
}

function isLogPage(value: unknown): value is LogPage {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    Array.isArray(row.items) &&
    row.items.every(isLogEntry) &&
    isNumber(row.total) &&
    isNumber(row.limit) &&
    isNumber(row.offset)
  );
}

function buildQuery(filters: LogFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    // An empty filter field must not become `event_name=`, which the API would read as a real
    // filter for the empty string and return nothing.
    if (value === undefined || value === null || value === "") {
      continue;
    }
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const fetchLogs = createAsyncThunk<
  LogPage,
  LogFilters | undefined,
  { rejectValue: string }
>("logs/fetch", async (filters, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/logs${buildQuery(filters ?? {})}`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isLogPage(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const logsSlice = createSlice({
  name: "logs",
  initialState: logsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchLogs.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchLogs.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.page = action.payload;
      })
      .addCase(fetchLogs.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      });
  },
});

export const logsReducer = logsSlice.reducer;
